import { api } from './api';

/**
 * Token Refresh Service
 * Handles automatic token refresh when the access token expires
 */
class TokenRefreshService {
  private refreshTimer: ReturnType<typeof setTimeout> | null = null;
  private isRefreshing = false;

  /**
   * Initialize automatic token refresh
   * Should be called after successful login
   */
  initializeTokenRefresh(expiresIn: number): void {
    this.clearRefreshTimer();

    // Refresh token 5 minutes before it expires
    const refreshTime = Math.max((expiresIn - 300) * 1000, 60000); // At least 1 minute

    this.refreshTimer = setTimeout(async () => {
      await this.refreshAccessToken();
    }, refreshTime);

    console.log(`Token refresh scheduled in ${refreshTime / 1000} seconds`);
  }

  /**
   * Manually refresh the access token
   */
  async refreshAccessToken(): Promise<boolean> {
    if (this.isRefreshing) {
      console.log('Token refresh already in progress');
      return false;
    }

    try {
      this.isRefreshing = true;
      const refreshToken = localStorage.getItem('refreshToken');

      if (!refreshToken) {
        console.error('No refresh token available');
        this.handleTokenExpired();
        return false;
      }

      const response = await api.refreshToken(refreshToken);

      // Update stored tokens
      localStorage.setItem('token', response.token);
      localStorage.setItem('refreshToken', response.refreshToken);
      localStorage.setItem('user', JSON.stringify(response.user));

      // Schedule next refresh
      this.initializeTokenRefresh(response.expiresIn);

      console.log('Token refreshed successfully');
      return true;

    } catch (error) {
      console.error('Failed to refresh token:', error);
      this.handleTokenExpired();
      return false;
    } finally {
      this.isRefreshing = false;
    }
  }

  /**
   * Handle token expiration - clear data and redirect to login
   */
  private handleTokenExpired(): void {
    this.clearRefreshTimer();
    localStorage.removeItem('token');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('user');

    // Dispatch event to notify app of token expiration
    window.dispatchEvent(new CustomEvent('auth-token-expired', {
      detail: { reason: 'Token expired or refresh failed' }
    }));
  }

  /**
   * Clear the refresh timer
   */
  clearRefreshTimer(): void {
    if (this.refreshTimer) {
      clearTimeout(this.refreshTimer);
      this.refreshTimer = null;
    }
  }

  /**
   * Logout and clear all tokens
   */
  async logout(): Promise<void> {
    try {
      await api.logout();
    } catch (error) {
      console.error('Logout API call failed:', error);
    } finally {
      this.clearRefreshTimer();
      localStorage.removeItem('token');
      localStorage.removeItem('refreshToken');
      localStorage.removeItem('user');
    }
  }
}

export const tokenRefreshService = new TokenRefreshService();
